import { MINUS_ICON_REDESIGN, PLUS_ICON_REDESIGN } from 'constants/filterIcons';
import useIdentifierCategories from 'hooks/identifiers/useIdentifierCategories';
import { capitalize } from 'lodash';
import moment from 'moment';
import React, { useState } from 'react';
import { getSelectedIdentifierRevision } from 'selectors/identifier';
import { useSelector } from 'store';

type UseCasesModels = {
  name: string;
  uuid: string;
  group: string;
  rules: {
    name: string;
    uuid: string;
    updated: string;
  }[];
};

type UseCaseItemComponentProps = {
  useCaseItem: UseCasesModels;
};

const UseCaseItem: React.FC<UseCaseItemComponentProps> = ({ useCaseItem }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const toggleIsExpanded = (): void => {
    setIsExpanded(!isExpanded);
  };

  return (
    <div className="flex flex-col gap-2 text-small">
      <div className="flex flex-row items-center gap-2 text-small">
        <button type="button" className="focus:outline-none" onClick={toggleIsExpanded}>
          {isExpanded ? MINUS_ICON_REDESIGN : PLUS_ICON_REDESIGN}
        </button>
        <a
          target="_blank"
          rel="noopener noreferrer"
          className="text-litlingo-primary-120 underline"
          href={`${window.location.origin}/global/model/${useCaseItem.uuid}`}
        >
          <span className="underline cursor-pointer">Use Cases &gt; {useCaseItem.name}</span>
        </a>

        {useCaseItem.group && (
          <span className="text-litlingo-gray-5">({capitalize(useCaseItem.group)})</span>
        )}
      </div>
      {isExpanded && (
        <div className="flex flex-col gap-1 pl-5">
          {useCaseItem.rules.map((r) => (
            <div key={r.uuid} className="flex flex-row gap-1">
              <svg
                width="11"
                height="11"
                viewBox="0 0 11 11"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M1 0V6C1 8.20914 2.79086 10 5 10H11" stroke="#5C5C5C" />
              </svg>
              <a
                target="_blank"
                rel="noopener noreferrer"
                className="text-litlingo-primary-120 underline"
                href={`${window.location.origin}/global/rule-group/${r.uuid}`}
              >
                <span className="underline cursor-pointer">{r.name}</span>
              </a>

              <span className="whitespace-no-wrap text-litlingo-gray-5">
                {' '}
                ({moment(r.updated).fromNow()})
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const IdentifierAssociatedModels: React.FC = () => {
  const identifierRevision = useSelector(getSelectedIdentifierRevision);
  const { data } = useIdentifierCategories(identifierRevision?.uuid || '');

  const useCases = data?.reduce<Record<string, UseCasesModels>>((acc, val) => {
    if (acc[val.model_uuid]) {
      if (!acc[val.model_uuid].rules.find((r) => r.uuid === val.rule?.rule_group_uuid)) {
        acc[val.model_uuid].rules.push({
          name: val.rule?.name || '',
          uuid: val.rule?.rule_group_uuid || '',
          updated: val.rule?.updated_at || '',
        });
      }
    } else {
      acc[val.model_uuid] = {
        name: val.model?.name || '',
        uuid: val.model_uuid || '',
        // @ts-ignore
        group: val.model.kind || '',
        rules: [
          {
            name: val.rule?.name || '',
            uuid: val.rule?.rule_group_uuid || '',
            updated: val.rule?.updated_at || '',
          },
        ],
      };
    }
    return acc;
  }, {});

  return (
    <div className="flex flex-col gap-1 bg-white">
      {useCases && Object.values(useCases).length > 0 ? (
        Object.values(useCases).map((u) => <UseCaseItem key={u.uuid} useCaseItem={u} />)
      ) : (
        <span>No associated models</span>
      )}
    </div>
  );
};

export default IdentifierAssociatedModels;
